import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import {
  staffLogin,
  staffPasskeyBeginEnroll,
  staffPasskeyFinishEnroll,
  staffPasskeyBeginLogin,
  staffPasskeyFinishLogin,
} from '../../api/staff';
import { useStaffAuthStore, type StaffSession } from '../../store/staffAuth';
import { useT } from '../../i18n';

// ANX-087 — вход в staff-админку (отдельный контур). Password сейчас;
// passkey-MFA добавится шагом 087a'.
export function StaffLoginView({ onAuthenticated }: { onAuthenticated: () => void }) {
  const t = useT();
  const setSession = useStaffAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function complete(session: StaffSession) {
    setSession(session);
    onAuthenticated();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const res = await staffLogin(email.trim().toLowerCase(), password);
      if (res.kind === 'session') {
        complete(res.session);
        return;
      }
      if (res.kind === 'enroll') {
        // Обязательная первичная регистрация passkey (087a').
        setStatus(t('staff.login.enrollPrompt'));
        const begin = await staffPasskeyBeginEnroll(res.enrollSession);
        const attestation = await startRegistration({ optionsJSON: begin.options.publicKey as never });
        complete(await staffPasskeyFinishEnroll(begin.session_token, attestation));
        return;
      }
      // kind === 'mfa' — второй шаг существующим passkey.
      setStatus(t('staff.login.mfaPrompt'));
      const begin = await staffPasskeyBeginLogin(res.mfaSession);
      const assertion = await startAuthentication({ optionsJSON: begin.options.publicKey as never });
      complete(await staffPasskeyFinishLogin(begin.session_token, assertion));
    } catch (err) {
      const code = err instanceof Error ? err.message : 'error';
      if (code === 'forbidden') setError(t('staff.login.suspended'));
      else if (code === 'auth_failed') setError(t('staff.login.failed'));
      else setError(t('staff.login.mfaFailed'));
    } finally {
      setBusy(false);
      setStatus(null);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-xl"
      >
        <div className="mb-5 flex items-center gap-2 text-slate-100">
          <ShieldCheck className="h-5 w-5 text-accent-400" aria-hidden="true" />
          <h1 className="text-lg font-semibold">{t('staff.login.title')}</h1>
        </div>

        <label className="mb-3 block text-sm text-slate-300">
          <span>{t('staff.login.email')}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            className="mt-1 block w-full rounded border border-slate-600 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-accent-500 focus:ring-accent-500"
          />
        </label>
        <label className="mb-4 block text-sm text-slate-300">
          <span>{t('staff.login.password')}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-1 block w-full rounded border border-slate-600 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-accent-500 focus:ring-accent-500"
          />
        </label>

        {status && <p className="mb-3 text-sm text-accent-300">{status}</p>}
        {error && (
          <p role="alert" className="mb-3 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !email || !password}
          className="w-full rounded-md bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {busy ? t('staff.login.signingIn') : t('staff.login.signIn')}
        </button>
      </form>
    </div>
  );
}
